import React, { useState } from 'react';

const About = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredLeader, setHoveredLeader] = useState(null);

  const colors = {
    white: '#ffffff',
    page: '#f5f9ff',
    pageSoft: '#eef6ff',

    navy: '#071a33',
    navyLight: '#163a63',

    blue: '#0879d1',
    blueBright: '#0ea5e9',
    cyan: '#22d3ee',

    text: '#0f2742',
    textLight: '#55708d',

    border: '#dbe9f6',
    borderBlue: '#b9ddf7',

    glass: 'rgba(255,255,255,0.78)',
    glassStrong: 'rgba(255,255,255,0.90)',

    shadow: 'rgba(23,83,132,0.10)',
  };

  const styles = {
    aboutSection: {
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      padding: '100px 5%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      background: `
        radial-gradient(
          circle at 8% 15%,
          rgba(14,165,233,0.10),
          transparent 25%
        ),
        radial-gradient(
          circle at 92% 35%,
          rgba(34,211,238,0.08),
          transparent 25%
        ),
        radial-gradient(
          circle at 50% 100%,
          rgba(8,121,209,0.08),
          transparent 32%
        ),
        ${colors.page}
      `,
      fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
      color: colors.text,
    },

    engineeringGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '48px 48px',
      animation: 'aboutGridMove 24s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    dotGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'radial-gradient(rgba(8,121,209,0.17) 1px, transparent 1px)',
      backgroundSize: '25px 25px',
      opacity: 0.25,
      animation: 'aboutDotMove 18s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    glowOne: {
      position: 'absolute',
      width: '420px',
      height: '420px',
      left: '-180px',
      top: '120px',
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(14,165,233,0.14), transparent 70%)',
      filter: 'blur(12px)',
      animation: 'aboutGlowOne 9s ease-in-out infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    glowTwo: {
      position: 'absolute',
      width: '450px',
      height: '450px',
      right: '-190px',
      bottom: '80px',
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(34,211,238,0.12), transparent 70%)',
      filter: 'blur(12px)',
      animation: 'aboutGlowTwo 11s ease-in-out infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    structureLineOne: {
      position: 'absolute',
      width: '350px',
      height: '1px',
      top: '20%',
      left: '-120px',
      background:
        'linear-gradient(90deg, transparent, rgba(8,121,209,0.35), transparent)',
      transform: 'rotate(-25deg)',
      animation: 'aboutLineOne 9s ease-in-out infinite',
      pointerEvents: 'none',
    },

    structureLineTwo: {
      position: 'absolute',
      width: '330px',
      height: '1px',
      top: '65%',
      right: '-110px',
      background:
        'linear-gradient(90deg, transparent, rgba(34,211,238,0.4), transparent)',
      transform: 'rotate(25deg)',
      animation: 'aboutLineTwo 10s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 5,
      width: '100%',
      maxWidth: '1220px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '65px',
    },

    header: {
      maxWidth: '900px',
      margin: '0 auto',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      animation: 'aboutFadeUp 0.9s ease both',
    },

    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      padding: '9px 16px',
      borderRadius: '999px',
      background: 'rgba(255,255,255,0.78)',
      border: `1px solid ${colors.borderBlue}`,
      color: colors.blue,
      fontSize: '0.76rem',
      fontWeight: '750',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      boxShadow: '0 8px 30px rgba(8,121,209,0.07)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
    },

    badgeDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.cyan,
      boxShadow: '0 0 0 5px rgba(34,211,238,0.11)',
      animation: 'aboutPulse 2s ease-in-out infinite',
    },

    heading: {
      margin: '24px 0 0',
      fontSize: 'clamp(2.5rem, 5vw, 4.4rem)',
      lineHeight: '1.02',
      letterSpacing: '-0.05em',
      fontWeight: '850',
      color: colors.navy,
    },

    gradientText: {
      background:
        'linear-gradient(100deg, #0879d1 0%, #0ea5e9 48%, #22d3ee 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      position: 'relative',
      display: 'inline-block',
    },

    underline: {
      position: 'absolute',
      left: '2%',
      right: '2%',
      bottom: '-8px',
      height: '8px',
      borderRadius: '20px',
      background:
        'linear-gradient(90deg, rgba(8,121,209,0.08), rgba(34,211,238,0.35), rgba(8,121,209,0.08))',
      animation: 'aboutUnderline 1.1s ease 0.6s both',
    },

    introText: {
      maxWidth: '790px',
      margin: '25px auto 0',
      color: colors.textLight,
      fontSize: '1.05rem',
      lineHeight: '1.8',
      fontWeight: '450',
    },

    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: '20px',
    },

    card: (isHovered) => ({
      position: 'relative',
      overflow: 'hidden',
      padding: '28px',
      minHeight: '310px',
      borderRadius: '22px',
      background: isHovered
        ? 'rgba(255,255,255,0.93)'
        : colors.glass,
      border: `1px solid ${isHovered ? colors.borderBlue : colors.border
        }`,
      boxShadow: isHovered
        ? '0 25px 60px rgba(8,121,209,0.15)'
        : '0 12px 35px rgba(23,83,132,0.07)',
      transform: isHovered
        ? 'translateY(-10px)'
        : 'translateY(0)',
      transition:
        'all 0.4s cubic-bezier(.2,.8,.2,1)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'default',
    }),

    cardGlow: (isHovered) => ({
      position: 'absolute',
      width: '190px',
      height: '190px',
      right: '-100px',
      top: '-100px',
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(14,165,233,0.20), transparent 70%)',
      opacity: isHovered ? 1 : 0.5,
      transition: 'opacity 0.4s ease',
      pointerEvents: 'none',
    }),

    numberBox: (isHovered) => ({
      width: '54px',
      height: '54px',
      borderRadius: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isHovered
        ? 'linear-gradient(135deg, #0879d1, #22d3ee)'
        : 'linear-gradient(135deg, rgba(8,121,209,0.10), rgba(34,211,238,0.13))',
      border: `1px solid ${isHovered ? 'transparent' : colors.borderBlue
        }`,
      color: isHovered ? '#ffffff' : colors.blue,
      fontWeight: '800',
      fontSize: '0.9rem',
      marginBottom: '22px',
      transition: 'all 0.35s ease',
      boxShadow: isHovered
        ? '0 10px 25px rgba(8,121,209,0.25)'
        : 'none',
    }),

    cardTitle: {
      margin: 0,
      fontSize: '1.25rem',
      lineHeight: '1.3',
      fontWeight: '750',
      color: colors.navy,
    },

    cardText: {
      margin: '12px 0 0',
      fontSize: '0.92rem',
      lineHeight: '1.7',
      color: colors.textLight,
    },

    cardBottomLine: (isHovered) => ({
      width: isHovered ? '100%' : '35%',
      height: '2px',
      marginTop: 'auto',
      background:
        'linear-gradient(90deg, #0879d1, #22d3ee, transparent)',
      transition: 'width 0.5s ease',
    }),

    leadershipBox: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '26px',
      padding: '42px',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '45px',
      alignItems: 'center',
      background: `
        radial-gradient(
          circle at 100% 0%,
          rgba(14,165,233,0.20),
          transparent 35%
        ),
        radial-gradient(
          circle at 0% 100%,
          rgba(34,211,238,0.10),
          transparent 30%
        ),
        linear-gradient(135deg, #071a33, #0b2949)
      `,
      boxShadow: '0 25px 65px rgba(7,26,51,0.22)',
      animation: 'aboutFadeUp 1s ease 0.3s both',
    },

    leadershipAccent: {
      position: 'absolute',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      right: '-130px',
      top: '-160px',
      border: '1px solid rgba(34,211,238,0.16)',
      boxShadow:
        '0 0 0 40px rgba(34,211,238,0.025), 0 0 0 80px rgba(34,211,238,0.02)',
      animation: 'leadershipOrbit 12s linear infinite',
      pointerEvents: 'none',
    },

    leadershipLabel: {
      color: '#38bdf8',
      textTransform: 'uppercase',
      fontSize: '0.74rem',
      letterSpacing: '0.16em',
      fontWeight: '750',
    },

    leadershipHeading: {
      margin: '10px 0 15px',
      color: '#ffffff',
      fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
      lineHeight: '1.1',
      letterSpacing: '-0.035em',
      fontWeight: '800',
    },

    leadershipText: {
      margin: 0,
      color: '#9bb0c5',
      fontSize: '0.94rem',
      lineHeight: '1.7',
      maxWidth: '520px',
    },

    leadersWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },

    leader: (isHovered) => ({
      position: 'relative',
      padding: '20px',
      borderRadius: '16px',
      background: isHovered
        ? 'rgba(255,255,255,0.09)'
        : 'rgba(255,255,255,0.045)',
      border: '1px solid rgba(255,255,255,0.07)',
      transform: isHovered
        ? 'translateX(7px)'
        : 'translateX(0)',
      transition: 'all 0.35s ease',
    }),

    leaderName: {
      color: '#ffffff',
      fontSize: '1.08rem',
      fontWeight: '750',
      lineHeight: '1.4',
    },

    leaderRole: {
      marginTop: '4px',
      color: '#38bdf8',
      fontSize: '0.82rem',
      fontWeight: '650',
    },

    leaderExperience: {
      marginTop: '9px',
      color: '#94a9be',
      fontSize: '0.83rem',
      lineHeight: '1.6',
    },

    divider: {
      width: '100%',
      height: '1px',
      background: 'rgba(255,255,255,0.08)',
    },
  };

  return (
    <section style={styles.aboutSection}>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes aboutGridMove {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 48px 48px;
          }
        }

        @keyframes aboutDotMove {
          0% {
            transform: translate3d(0, 0, 0);
          }
          50% {
            transform: translate3d(12px, -12px, 0);
          }
          100% {
            transform: translate3d(0, 0, 0);
          }
        }

        @keyframes aboutGlowOne {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(45px, -20px) scale(1.08);
          }
        }

        @keyframes aboutGlowTwo {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-40px, 25px) scale(1.1);
          }
        }

        @keyframes aboutLineOne {
          0%, 100% {
            transform: translateX(0) rotate(-25deg);
            opacity: 0.2;
          }
          50% {
            transform: translateX(150px) rotate(-25deg);
            opacity: 0.7;
          }
        }

        @keyframes aboutLineTwo {
          0%, 100% {
            transform: translateX(0) rotate(25deg);
            opacity: 0.2;
          }
          50% {
            transform: translateX(-140px) rotate(25deg);
            opacity: 0.75;
          }
        }

        @keyframes aboutFadeUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes aboutPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 5px rgba(34,211,238,0.11);
          }
          50% {
            transform: scale(1.25);
            box-shadow: 0 0 0 9px rgba(34,211,238,0.035);
          }
        }

        @keyframes aboutUnderline {
          from {
            transform: scaleX(0);
            opacity: 0;
          }
          to {
            transform: scaleX(1);
            opacity: 1;
          }
        }

        @keyframes leadershipOrbit {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 900px) {
          .about-cards-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .about-leadership {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 700px) {
          .about-section {
            padding: 70px 20px !important;
          }

          .about-cards-grid {
            grid-template-columns: 1fr !important;
            gap: 15px !important;
          }

          .about-card {
            min-height: auto !important;
            padding: 24px !important;
          }

          .about-leadership {
            padding: 28px 22px !important;
            border-radius: 20px !important;
            gap: 30px !important;
          }

          .about-heading {
            font-size: 2.65rem !important;
          }

          .about-intro {
            font-size: 0.94rem !important;
            line-height: 1.7 !important;
          }
        }

        @media (max-width: 480px) {
          .about-section {
            padding: 60px 15px !important;
          }

          .about-heading {
            font-size: 2.35rem !important;
          }

          .about-badge {
            font-size: 0.65rem !important;
            padding: 8px 12px !important;
          }

          .about-card-title {
            font-size: 1.1rem !important;
          }

          .about-card-text {
            font-size: 0.86rem !important;
          }

          .about-leadership-heading {
            font-size: 1.8rem !important;
          }

          .about-leader-name {
            font-size: 1rem !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* Background */}
      <div style={styles.engineeringGrid} />
      <div style={styles.dotGrid} />
      {/* <div style={styles.glowOne} />
      <div style={styles.glowTwo} /> */}
      <div style={styles.structureLineOne} />
      <div style={styles.structureLineTwo} />

      <div
        style={styles.container}
        className="about-section"
      >
        {/* ================= HEADER ================= */}

        <div style={styles.header}>
          <div
            style={styles.badge}
            className="about-badge"
          >
            <span style={styles.badgeDot} />
            About SANSA CONSULTANT
          </div>

          <h2
            style={styles.heading}
            className="about-heading"
          >
            Pioneering Precision in{' '}
            <span style={styles.gradientText}>
              Structural Engineering
              <span style={styles.underline} />
            </span>
          </h2>

          <p
            style={styles.introText}
            className="about-intro"
          >
            Established in May 2026 in Pune, SANSA CONSULTANT
            INDIA LLP is an elite engineering service provider
            delivering technically sound, practical, and
            optimized solutions across industrial, commercial,
            and infrastructure projects.
          </p>
        </div>

        {/* ================= CORE PILLARS ================= */}

        {/* <div
          style={styles.cardsGrid}
          className="about-cards-grid"
        >
        
          <div
            style={styles.card(hoveredCard === 1)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(1)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 1)} />

            <div
              style={styles.numberBox(hoveredCard === 1)}
            >
              01
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Our Core Commitment
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              We deliver safe, reliable, economical, and
              code-compliant engineering solutions. From concept
              and design reviews to complete construction
              execution, we support owners, EPC contractors, and
              architects while enforcing absolute data secrecy.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 1
              )}
            />
          </div>

          <div
            style={styles.card(hoveredCard === 2)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(2)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 2)} />

            <div
              style={styles.numberBox(hoveredCard === 2)}
            >
              02
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Advanced Digital Workflows
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              Leveraging state-of-the-art software tools
              including STAAD.Pro for deep analysis, IDEA StatiCa
              and RAM Connection for connection verification, and
              Tekla Structures for 3D modeling and
              fabrication-ready shop drawings.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 2
              )}
            />
          </div>

          
          <div
            style={styles.card(hoveredCard === 3)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(3)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 3)} />

            <div
              style={styles.numberBox(hoveredCard === 3)}
            >
              03
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Land Development & Survey
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              Specialized conversion of raw survey data into
              comprehensive finished survey drawings,
              topographic maps, boundary layouts, drainage
              planning, and precise Civil 3D infrastructure
              models.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 3
              )}
            />
          </div>
        </div> */}

        {/* ================= CORE PILLARS ================= */}

        <div
          style={styles.cardsGrid}
          className="about-cards-grid"
        >
          {/* =================================================
      CARD 01 — CORE COMMITMENT
  ================================================= */}

          <div
            style={styles.card(hoveredCard === 1)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(1)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 1)} />

            <div
              style={styles.numberBox(hoveredCard === 1)}
            >
              01
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Our Core Commitment
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              We deliver safe, reliable, economical, and
              code-compliant engineering solutions. From concept
              and design reviews to construction execution, we
              support owners, EPC contractors, and architects
              while maintaining strict data confidentiality.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 1
              )}
            />
          </div>

          {/* =================================================
      CARD 02 — DIGITAL ENGINEERING
  ================================================= */}

          <div
            style={styles.card(hoveredCard === 2)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(2)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 2)} />

            <div
              style={styles.numberBox(hoveredCard === 2)}
            >
              02
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Advanced Digital Engineering
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              We use advanced engineering platforms including
              STAAD.Pro, IDEA StatiCa, RAM Connection, and
              Tekla Structures for structural analysis,
              connection verification, 3D modelling, detailing,
              and fabrication-ready documentation.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 2
              )}
            />
          </div>

          {/* =================================================
      CARD 03 — LAND SURVEY
  ================================================= */}

          <div
            style={styles.card(hoveredCard === 3)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(3)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 3)} />

            <div
              style={styles.numberBox(hoveredCard === 3)}
            >
              03
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Land Survey & Geospatial Services
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              We convert raw field and survey information into
              accurate boundary, topographic, finished survey,
              contour, and site documentation for engineering,
              planning, and construction applications.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 3
              )}
            />
          </div>

          {/* =================================================
      CARD 04 — LAND DEVELOPMENT
  ================================================= */}

          <div
            style={styles.card(hoveredCard === 4)}
            className="about-card"
            onMouseEnter={() => setHoveredCard(4)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={styles.cardGlow(hoveredCard === 4)} />

            <div
              style={styles.numberBox(hoveredCard === 4)}
            >
              04
            </div>

            <h3
              style={styles.cardTitle}
              className="about-card-title"
            >
              Land Development
            </h3>

            <p
              style={styles.cardText}
              className="about-card-text"
            >
              We transform development concepts into practical
              and coordinated site plans covering grading,
              drainage, utilities, road layouts, site
              infrastructure, and Civil 3D-based development
              documentation.
            </p>

            <div
              style={styles.cardBottomLine(
                hoveredCard === 4
              )}
            />
          </div>



        </div>

        {/* ================= LEADERSHIP ================= */}

        <div
          style={styles.leadershipBox}
          className="about-leadership"
        >
          <div style={styles.leadershipAccent} />

          {/* LEFT */}
          <div>
            <span style={styles.leadershipLabel}>
              Executive Leadership
            </span>

            <h3
              style={styles.leadershipHeading}
              className="about-leadership-heading"
            >
              Guided by Decades of Engineering Mastery
            </h3>

            <p style={styles.leadershipText}>
              Our firm is directly spearheaded by seasoned
              industry directors committed to transforming
              intricate design challenges into buildable
              reality.
            </p>
          </div>

          {/* RIGHT */}
          <div style={styles.leadersWrapper}>
            {/* LEADER 1 */}
            <div
              style={styles.leader(
                hoveredLeader === 1
              )}
              onMouseEnter={() => setHoveredLeader(1)}
              onMouseLeave={() => setHoveredLeader(null)}
            >
              <div
                style={styles.leaderName}
                className="about-leader-name"
              >
                Sangmeshwar Vishwanathrao Karlekar
              </div>

              <div style={styles.leaderRole}>
                Director & Principal Structural Engineer
              </div>

              <div style={styles.leaderExperience}>
                25+ years of extensive, varied professional
                experience in complex structural engineering
                and large-scale project execution.
              </div>
            </div>

            <div style={styles.divider} />

            {/* LEADER 2 */}
            <div
              style={styles.leader(
                hoveredLeader === 2
              )}
              onMouseEnter={() => setHoveredLeader(2)}
              onMouseLeave={() => setHoveredLeader(null)}
            >
              <div
                style={styles.leaderName}
                className="about-leader-name"
              >
                Santosh Shejwal
              </div>

              <div style={styles.leaderRole}>
                Director & Structural Consultant
              </div>

              <div style={styles.leaderExperience}>
                18+ years of dedicated professional expertise
                in structural design excellence, safety
                optimization, and site execution.
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;